/***************************************************************************/
"use client";
/***************************************************************************/

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../../utils/css";

const alertVariants = cva("relative w-full rounded border flex flex-row", {
  variants: {
    variant: {
      default: "bg-background text-foreground",
      success: "border-success/50 border-success",
      info: "border-info/50",
      warning: "border-warning/50",
      error: "border-error/50",
    },
    size: {
      default: "p-3 text-sm gap-2",
      sm: "p-2 text-xs gap-1.5",
      lg: "p-4 rounded-md gap-3",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const AlertContext = React.createContext<{
  variant?:
    | "default"
    | "success"
    | "info"
    | "warning"
    | "error"
    | undefined
    | null;
  size?: "default" | "sm" | "lg" | undefined | null;
}>({ variant: "default", size: "default" });

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, size = "default", ...props }, ref) => (
  <AlertContext.Provider value={{ variant, size }}>
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant, size }), className)}
      {...props}
    />
  </AlertContext.Provider>
));
Alert.displayName = "Alert";

export { Alert, AlertContext, alertVariants };
